import { computed, inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { filter, map, Observable, take } from 'rxjs';

import { apiResource, UserStore } from '@hmax-order/frontend/state';
import { CompanyHeader } from '@hmax-order/shared/types';

@Injectable({ providedIn: 'root' })
export class CompanyHeaderService {
  private readonly _injector = inject(Injector);

  private readonly _userStore = inject(UserStore);

  private readonly _resource = apiResource<CompanyHeader>(
    // Ocioso sem empresa atual: a API exige `companyId` do token.
    () => (this._userStore.getCurrentCompanyId() ? 'company-header' : undefined),
    { defaultValue: {} },
  );

  /** Passa por `hasValue()` porque ler `value()` com o resource em erro lança, mesmo com `defaultValue`. */
  readonly companyHeader = computed<CompanyHeader>(() => (this._resource.hasValue() ? this._resource.value() : {}));
  readonly isLoading = this._resource.isLoading;
  readonly error = this._resource.error;

  /**
   * Emite uma vez quando o cabeçalho da empresa termina de carregar.
   *
   * Existe para o resolver de rota, que precisa segurar a navegação até o
   * dado chegar. Erro não rejeita: o resolver não pode travar o login por
   * causa disso.
   */
  get(): Observable<CompanyHeader> {
    return toObservable(this._resource.status, { injector: this._injector }).pipe(
      // `idle` também encerra: recurso ocioso (sem empresa atual) nunca vai carregar,
      // e sem ele aqui o resolver que espera esta emissão pendura a navegação para sempre.
      filter((status) => status === 'resolved' || status === 'error' || status === 'local' || status === 'idle'),
      take(1),
      map(() => this.companyHeader()),
    );
  }

  /** Refaz o fetch. */

  reload(): void {
    this._resource.reload();
  }

  /** Atualização otimista local, sem ida ao servidor (ex.: logo recém enviado). */
  updateLocal(partial: Partial<CompanyHeader>): void {
    // `update` leria `value()` cru, que lança em estado de erro. `companyHeader`
    // já passa por `hasValue()`.
    this._resource.set({ ...this.companyHeader(), ...partial });
  }

  /** Esvazia o cabeçalho local. Usar no logout/troca de empresa. */
  clear(): void {
    this._resource.set({});
  }
}
